import React, { useEffect, useState } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Info } from 'lucide-react';

import { type TypeProject } from '../../../common/dataTypes';
import { createTanstackTable } from '../../../common/utils';
import { useGetReceivedSharingInvitations } from '../../../hooks/tanstack/useReceviedSharingInvitations';
import {
  useGetRouteParams,
  useGetSearchResults,
} from '../../../hooks/tanstack/useUtilsHook';
import TanstackTable from '../../TanstackTable';
import SentInvitationDialog from '../SentInvitationDialog';

export type DataRow = {
  id: number;
  sender: {
    name?: string;
    email: string;
  };
  receiver: {
    name?: string;
    email: string;
  };
  description: string;
  project: TypeProject;
  model: string;
  acceptedAt?: string;
  createdAt: string;
};

const columnHelper = createColumnHelper<DataRow>();

const columns = [
  columnHelper.accessor('sender', {
    cell: (info) => {
      const { name, email } = info.getValue();

      return (
        <div className="group flex flex-col items-start text-start">
          <p className="w-full font-semibold">{name}</p>
          <p className="text-sm text-[#666]">{email}</p>
        </div>
      );
    },
    header: () => (
      <div className="flex items-center gap-2">
        <p>Receiver</p>
      </div>
    ),
  }),
  columnHelper.accessor('project', {
    id: 'project',
    cell: (info) => {
      const project = info.getValue();

      return project ? (
        <div className="w-full">{project.name}</div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      );
    },
    header: () => <div>Project</div>,
  }),
  columnHelper.accessor('model', {
    id: 'model',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">{info.getValue()}</div>
      ) : (
        <div className="text-gray-400 w-full text-start">--</div>
      ),
    header: () => <div>Model</div>,
  }),
  columnHelper.accessor('receiver', {
    cell: (info) => {
      const value = info.getValue();

      return (
        <div className="group flex flex-col items-start text-start">
          <p className="w-full font-semibold">{value?.name}</p>
          <p className="text-sm text-[#666]">{value?.email}</p>
        </div>
      );
    },
    header: () => (
      <div className="flex items-center gap-2">
        <p>Sender</p>
      </div>
    ),
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">
          {format(info.getValue() as string, 'y/MM/dd')}
        </div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      ),
    sortingFn: (a, b) =>
      new Date(a.original.createdAt as string).getTime() -
      new Date(b.original.createdAt as string).getTime(),
    header: () => <div>Date Received</div>,
  }),
  columnHelper.accessor('acceptedAt', {
    id: 'acceptedAt',
    cell: (info) => {
      const [openDialog, setOpenDialog] = useState(false);

      const modelName = info.row.original.model;
      const modelDescription = info.row.original.description;
      const projectName = info.row.original.project.name;
      const sender = info.row.original.sender;
      const receiver = info.row.original.receiver;

      const handleOpenDialog = () => {
        setOpenDialog(true);
      };

      const handleCloseDialog = () => {
        setOpenDialog(false);
      };

      return (
        <div className="flex min-w-[200px] justify-between">
          {info.getValue() ? (
            <p>{format(info.getValue() as string, 'y/MM/dd')}</p>
          ) : (
            <p>--</p>
          )}
          <div className="flex gap-2">
            <Info color="#2196F3" onClick={handleOpenDialog} />
          </div>
          <SentInvitationDialog
            open={openDialog}
            handleOnClose={handleCloseDialog}
            data={{
              name: modelName,
              description: modelDescription,
              from: sender.name ?? '',
              to: receiver.name ?? '',
              projectName: projectName ?? '',
            }}
          />
        </div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.acceptedAt as string).getTime() -
      new Date(b.original.acceptedAt as string).getTime(),
    header: () => <div>Date Accepted</div>,
    size: 200,
  }),
];

const ReceivedInvitationsTable = ({ searchKey }: { searchKey: string }) => {
  const [data, setData] = useState<DataRow[]>([]);

  const { orgId } = useGetRouteParams();

  const {
    data: receiveSharingInvitations,
    isLoading: isFetchingReceivedSharingInvitations,
    isRefetching: isRefetchingReceivedSharingInvitations,
  } = useGetReceivedSharingInvitations(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const searchResults = useGetSearchResults<DataRow>({
    searchValue: searchKey,
    data,
    filterFields: [
      'project.name',
      'project.description',
      'model',
      'sender.email',
      'sender.name',
      'receiver.name',
      'receiver.email',
      'shared_by.full_name',
    ],
  });

  useEffect(() => {
    if (receiveSharingInvitations) {
      setData(
        receiveSharingInvitations.map((r) => ({
          id: r.id,
          sender: {
            name: r.shared_by.first_name + ' ' + r.shared_by.last_name,
            email: r.shared_by.email,
          },
          receiver: {
            name: r.receiver.full_name,
            email: r.receiver.email,
          },
          project: r.project,
          model: r.model.name,
          description: r.model.description,
          acceptedAt: r.created_at,
          createdAt: r.created_at,
        })) as DataRow[]
      );
    }
  }, [receiveSharingInvitations]);

  const table = createTanstackTable({
    data: searchResults ?? data,
    columns,
    defaultSorting: {
      id: 'createdAt',
      desc: true,
    },
  });

  const isLoading =
    isFetchingReceivedSharingInvitations ||
    isRefetchingReceivedSharingInvitations;

  return (
    <div className="w-full">
      <TanstackTable
        table={table}
        isLoading={isLoading}
        emptyState={searchKey ? 'No results' : 'No invitations available'}
      />
    </div>
  );
};

export default ReceivedInvitationsTable;
